/**
 * WidgetErrorFallback — inline error UI rendered by WidgetErrorBoundary.
 *
 * Rules:
 *   - `role="alert"` so screen readers announce the failure immediately.
 *   - Retry button ≥44×44 px via parent padding (WCAG 2.1 AA).
 *   - No `transition-all` — uses `transition-colors duration-150` only.
 *   - AlertCircle icon — no animation.
 *   - All copy via i18n keys (`dashboard:error.*`).
 *   - RTL: logical properties `ps-*`, `pe-*` (no physical left/right).
 */
import type { FallbackProps } from "react-error-boundary";
import type { TranslateFn } from "#/lib/i18n.js";

function AlertCircleIcon() {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M23 4v6h-6" />
      <path d="M20.49 15A9 9 0 1 1 18.36 5.64L23 10" />
    </svg>
  );
}

export interface WidgetErrorFallbackProps extends FallbackProps {
  widgetName?: string;
  t: TranslateFn;
}

export function WidgetErrorFallback({
  error,
  resetErrorBoundary,
  widgetName,
  t,
}: WidgetErrorFallbackProps) {
  const message =
    error instanceof Error && error.message
      ? error.message
      : t("dashboard:error.genericBody");

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex flex-col items-center justify-center gap-3 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-6 text-center min-h-[120px]"
    >
      <span className="text-red-500 dark:text-red-400">
        <AlertCircleIcon />
      </span>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-[var(--color-fg-default)]">
          {widgetName
            ? t("dashboard:error.headline", { widget: widgetName })
            : t("dashboard:error.headlineGeneric")}
        </p>
        <p className="text-xs text-[var(--color-fg-muted)]">
          {t("dashboard:error.body")}
        </p>
      </div>
      {/* Touch target ≥44×44 via p-3 */}
      <button
        type="button"
        onClick={resetErrorBoundary}
        className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-3 text-xs font-medium text-[var(--color-fg-default)] hover:bg-[var(--color-bg-surface-elevated)] focus-visible:outline-none focus-visible:ring-2 transition-colors duration-150"
        style={{ minHeight: "44px", minWidth: "44px" }}
      >
        <RefreshIcon />
        {t("dashboard:error.retry")}
      </button>
    </div>
  );
}
